import { Injectable } from '@angular/core';

import { AuthResponse } from '../models/auth-response';

@Injectable({ providedIn: 'root' })
export class TokenStorageService {
  private readonly accessKey = 'htally_access';
  private readonly refreshKey = 'htally_refresh';
  private readonly expiryKey = 'htally_access_exp';
  private readonly refreshExpiryKey = 'htally_refresh_exp';
  private readonly storageModeKey = 'htally_auth_storage_mode';

  saveTokens(response: AuthResponse, options?: { persistent?: boolean }): void {
    const storage = this.getTargetStorage(options?.persistent ?? this.prefersPersistentSession);
    if (!storage) {
      return;
    }

    this.clearStoredTokenValues();

    storage.setItem(this.accessKey, response.token);
    storage.setItem(this.refreshKey, response.refreshToken);
    storage.setItem(this.expiryKey, this.calculateExpiry(response.expiresIn));
    storage.setItem(this.refreshExpiryKey, this.calculateExpiry(response.refreshExpiresIn));
    storage.setItem(this.storageModeKey, storage === this.getLocalStorage() ? 'local' : 'session');
  }

  get accessToken(): string | null {
    return this.getFromStorage(this.accessKey);
  }

  get refreshToken(): string | null {
    return this.getFromStorage(this.refreshKey);
  }

  get isAccessExpired(): boolean {
    return this.isExpired(this.expiryKey);
  }

  get isRefreshExpired(): boolean {
    return this.isExpired(this.refreshExpiryKey);
  }

  get hasValidAccessToken(): boolean {
    return !!this.accessToken && !this.isAccessExpired;
  }

  get hasValidRefreshToken(): boolean {
    return !!this.refreshToken && !this.isRefreshExpired;
  }

  get hasActiveSession(): boolean {
    return this.hasValidAccessToken || this.hasValidRefreshToken;
  }

  get prefersPersistentSession(): boolean {
    return this.resolveActiveStorageMode() === 'local';
  }

  get accessExpiryTime(): number | null {
    return this.getExpiryTime(this.expiryKey);
  }

  get refreshExpiryTime(): number | null {
    return this.getExpiryTime(this.refreshExpiryKey);
  }

  clear(): void {
    this.clearStoredTokenValues();
  }

  private calculateExpiry(expiresInSeconds: number): string {
    return (Date.now() + expiresInSeconds * 1000).toString();
  }

  private isExpired(key: string): boolean {
    const expiresAt = this.getExpiryTime(key);
    return !expiresAt || Date.now() > expiresAt;
  }

  private getExpiryTime(key: string): number | null {
    const storage = this.getStorage();
    if (!storage) {
      return null;
    }

    const expiresAt = Number(storage.getItem(key) ?? 0);
    return Number.isFinite(expiresAt) && expiresAt > 0 ? expiresAt : null;
  }

  private getFromStorage(key: string): string | null {
    const storage = this.getStorage();
    return storage?.getItem(key) ?? null;
  }

  private getStorage(): Storage | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const sessionStorageRef = this.getSessionStorage();
    const localStorageRef = this.getLocalStorage();
    const mode = this.resolveActiveStorageMode();

    if (mode === 'session') {
      return sessionStorageRef;
    }

    if (mode === 'local') {
      return localStorageRef;
    }

    if (this.hasStoredTokenValues(sessionStorageRef)) {
      return sessionStorageRef;
    }

    if (this.hasStoredTokenValues(localStorageRef)) {
      return localStorageRef;
    }

    return sessionStorageRef ?? localStorageRef;
  }

  private getTargetStorage(persistent: boolean): Storage | null {
    if (typeof window === 'undefined') {
      return null;
    }

    return persistent ? this.getLocalStorage() ?? this.getSessionStorage() : this.getSessionStorage() ?? this.getLocalStorage();
  }

  private resolveActiveStorageMode(): 'session' | 'local' | null {
    const sessionStorageRef = this.getSessionStorage();
    const localStorageRef = this.getLocalStorage();
    const sessionMode = `${sessionStorageRef?.getItem(this.storageModeKey) ?? ''}`.trim();
    const localMode = `${localStorageRef?.getItem(this.storageModeKey) ?? ''}`.trim();

    if (sessionMode === 'session') {
      return 'session';
    }

    if (localMode === 'local') {
      return 'local';
    }

    return null;
  }

  private clearStoredTokenValues(): void {
    [this.getSessionStorage(), this.getLocalStorage()].forEach(storage => {
      if (!storage) {
        return;
      }

      [this.accessKey, this.refreshKey, this.expiryKey, this.refreshExpiryKey, this.storageModeKey].forEach(key =>
        storage.removeItem(key)
      );
    });
  }

  private hasStoredTokenValues(storage: Storage | null): boolean {
    if (!storage) {
      return false;
    }

    return [this.accessKey, this.refreshKey, this.expiryKey, this.refreshExpiryKey].some(key => !!storage.getItem(key));
  }

  private getSessionStorage(): Storage | null {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  }

  private getLocalStorage(): Storage | null {
    return typeof window === 'undefined' ? null : window.localStorage;
  }
}
